/**
 * Filter Builder
 * Build Algolia filter strings
 */

import { state } from '../core/state.js';
import { EUROPEAN_COUNTRIES, UK_COUNTRIES } from '../core/constants.js';
import { buildGenderFilterExpression } from './gender-filter.js';
import { buildStateFilterFromNames } from '../decision-engine/plan-builder.js';

// Dependencies injected from index.js (for potential future use)
let getSelectedGender = null;

/**
 * Set UI dependencies (called from index.js after modules loaded)
 * @param {Object} deps
 */
export function setDependencies(deps) {
  getSelectedGender = deps.getSelectedGender;
}

/**
 * Build brand filter for 'brands' (array) field: match records where brands array contains any of the selected values
 * @param {string[]} brands
 * @returns {string|null}
 */
export function buildBrandFilter(brands) {
  if (!brands || brands.length === 0) return null;
  return '(' + brands.map((b) => `brands:"${b}"`).join(' OR ') + ')';
}

/**
 * Get state filter string when current search is state-only (no city).
 * @returns {string|null}
 */
export function getStateFilterString() {
  if (!state.isStateSearch) return null;
  return buildStateFilterFromNames(
    state.searchStateCode,
    state.searchStateName
  );
}

/**
 * Build filter for Europe/UK region (Scotland, England, UK, London) or Canada.
 * Individual UK countries (England, Scotland, etc.) filter by specific country.
 * UK-wide searches combine all UK constituent countries.
 * @returns {string|null}
 */
export function buildRegionFilterString() {
  if (!state.isExcludeUSSearch) return null;

  const ukAll = UK_COUNTRIES.map(c => `address.map.country:"${c}"`).join(" OR ");
  const ca =
    `address.map.country:"Canada" OR address.map.country:"CA"`;

  // Individual UK country search (England, Scotland, etc.) → specific country only
  if (state.ukCountryFilter) {
    return `(address.map.country:"${state.ukCountryFilter}")`;
  }

  // UK city/county with stateValue (legacy safety net)
  if (state.ukStateValue) {
    const statePart = buildStateFilterFromNames(null, state.ukStateValue);
    if (statePart) {
      return `(${ukAll}) AND ${statePart}`;
    }
  }

  const r = state.regionFilter;
  if (!r) return null;

  if (r === 'canada') {
    return `(${ca})`;
  }

  // UK-wide ("UK"), London, or other UK region → all UK countries
  return `(${ukAll})`;
}

/**
 * Build Algolia filters
 * @param {boolean} [skipStateFilter=false] - when true, omit state filter (e.g. state step adds its own via step.stateFilter)
 * @returns {string|undefined}
 */
export function buildFilters(skipStateFilter) {
  const filters = [];

  // Global exclusion: never show camps from Australia
  filters.push(`NOT address.map.country:"Australia"`);

  // Live filter — embedded mode skips it on the initial search (client-side
  // auto-fallback in search-executor); full mode always sends live:true (monolith
  // parity). Inline DOM check to avoid a filter-builder → modes/embedded →
  // search-executor → filter-builder import cycle.
  const isEmbedded = !!document.querySelector('[data-algolia-mode="embedded"]');
  if (state.live === true && !(state.isInitialSearch && isEmbedded)) {
    filters.push(`live:true`);
  }

  // Sport filter - use explicit sportSlug or implicit from dual search
  const effectiveSportSlug = state.sportSlug.length > 0
    ? state.sportSlug
    : (state.implicitSportSlug || []);
  if (effectiveSportSlug.length > 0) {
    const sportFilter = effectiveSportSlug
      .map((slug) => `sportSlug:"${slug}"`)
      .join(' OR ');
    filters.push(`(${sportFilter})`);
  }

  // Types filter: selected types OR _empty (camps with no feeCodes at index time)
  if (state.types.length > 0) {
    const parts = state.types.map((t) => `feeCodes:"${t}"`);
    parts.push(`feeCodes:"_empty"`);
    filters.push(`(${parts.join(' OR ')})`);
  }

  // Gender filter - new logic based on selectedGender and checkboxes
  const genderExpr = buildGenderFilterExpression();
  if (genderExpr) {
    filters.push(genderExpr);
  }

  // Skills filter: skills (array) field
  if (state.skills.length > 0) {
    const parts = state.skills.map((s) => `skills:"${s}"`);
    filters.push(`(${parts.join(' OR ')})`);
  }

  // Brand filter: brands (array) field
  if (state.brand.length > 0) {
    const brandFilter = buildBrandFilter(state.brand);
    if (brandFilter) {
      filters.push(brandFilter);
    }
  } else if (state.brandExclude && state.brandExclude.length > 0) {
    // "All other brands" mode: exclude the specific brand
    state.brandExclude.forEach((b) => filters.push(`NOT brands:"${b}"`));
  }

  // Hidden tier filter
  if (!!state.tier && state.tier.length > 0) {
    const tierFilter = state.tier.map((t) => `tier:"${t}"`).join(' OR ');
    filters.push(`(${tierFilter})`);
  }

  // Age filter
  if (state.age) {
    // Special handling for "22+" (Adult)
    // Use OR to cover both cases: camps starting from 22+ and camps accepting up to 22+
    if (state.age === '22+') {
      filters.push(`(minAge >= 22 OR maxAge >= 22)`);
    } else {
      const age = parseInt(state.age);
      if (!isNaN(age)) {
        filters.push(`minAge <= ${age} AND maxAge >= ${age}`);
      }
    }
  }

  // Europe/UK region: Scotland/England/UK/London/Canada use region filter (no radius except London uses coords+radius)
  const regionFilterStr = buildRegionFilterString();
  if (regionFilterStr) {
    filters.push(regionFilterStr);
  } else if (state.isExcludeUSSearch) {
    // Europe only: use whitelist of European countries
    const euroFilter = EUROPEAN_COUNTRIES.map(
      (c) => `address.map.country:"${c}"`
    ).join(' OR ');
    filters.push(`(${euroFilter})`);
  }

  const base = filters.join(' AND ');
  const stateFilter = skipStateFilter ? null : getStateFilterString();
  let out = base;
  if (stateFilter) out = out ? `${out} AND ${stateFilter}` : stateFilter;
  return out || undefined;
}

/**
 * Build filters excluding one or more types (for facet requests that need full option list).
 * @param {string|string[]} exclude - "brand", "types", or ["brand", "types"] to exclude both so lists are not narrowed
 * @returns {string|undefined}
 */
export function buildFiltersWithout(exclude) {
  const set = new Set(Array.isArray(exclude) ? exclude : [exclude]);
  const filters = [];

  // Global exclusion: never show camps from Australia
  filters.push(`NOT address.map.country:"Australia"`);

  if (!set.has('live') && state.live === true) {
    filters.push('live:true');
  }
  const effectiveSportSlug = state.sportSlug.length > 0
    ? state.sportSlug
    : (state.implicitSportSlug || []);
  if (!set.has('sportSlug') && effectiveSportSlug.length > 0) {
    filters.push(
      '(' + effectiveSportSlug.map((s) => `sportSlug:"${s}"`).join(' OR ') + ')'
    );
  }
  if (!set.has('types') && state.types.length > 0) {
    filters.push(
      '(' + state.types.map((t) => `feeCodes:"${t}"`).join(' OR ') + ')'
    );
  }
  if (!set.has('gender')) {
    const genderExpr = buildGenderFilterExpression();
    if (genderExpr) filters.push(genderExpr);
  }
  if (!set.has('skills') && state.skills.length > 0) {
    filters.push(
      '(' + state.skills.map((s) => `skills:"${s}"`).join(' OR ') + ')'
    );
  }
  if (!set.has('brand')) {
    if (state.brand.length > 0) {
      const brandFilter = buildBrandFilter(state.brand);
      if (brandFilter) filters.push(brandFilter);
    } else if (state.brandExclude && state.brandExclude.length > 0) {
      state.brandExclude.forEach((b) => filters.push(`NOT brands:"${b}"`));
    }
  }
  if (!set.has('tier') && !!state.tier && state.tier.length > 0) {
    filters.push(
      '(' + state.tier.map((t) => `tier:"${t}"`).join(' OR ') + ')'
    );
  }
  if (!set.has('age') && state.age) {
    if (state.age === '22+') {
      filters.push('(minAge >= 22 OR maxAge >= 22)');
    } else {
      const age = parseInt(state.age, 10);
      if (!isNaN(age)) {
        filters.push(`minAge <= ${age} AND maxAge >= ${age}`);
      }
    }
  }
  const regionFilterStr = buildRegionFilterString();
  if (regionFilterStr) {
    filters.push(regionFilterStr);
  } else if (state.isExcludeUSSearch) {
    // Europe only: use whitelist of European countries
    const euroFilter = EUROPEAN_COUNTRIES.map(
      (c) => `address.map.country:"${c}"`
    ).join(' OR ');
    filters.push(`(${euroFilter})`);
  }
  return filters.join(' AND ') || undefined;
}

console.log('📦 Filter builder module loaded');
